import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { comics } from "@/mocks/comics";
import { formatCount, relatedComics } from "@/utils/comic";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";
import ComicCard from "@/components/comic/ComicCard";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => comics.find((item) => item.id === id), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [chapterOpen, setChapterOpen] = useState(true);
  const [toast, setToast] = useState("");

  const related = useMemo(() => (comic ? relatedComics(comics, comic, 4) : []), [comic]);
  const favorited = comic ? isFavorite(comic.id) : false;

  useSeo({
    title: comic
      ? `${comic.title} - 漫画详情 - 韩漫免费漫画在线观看阅读双女主`
      : "漫画不存在 - 韩漫免费漫画在线观看阅读双女主",
    description: comic
      ? `${comic.title}：${comic.intro.slice(0, 70)}… 双女主韩漫免费在线观看，状态：${comic.status}。`
      : "该漫画不存在或已下架，返回首页浏览更多双女主韩漫。",
    keywords: comic ? `${comic.title},双女主漫画,韩漫在线阅读,${comic.genres.join(",")}` : "双女主韩漫",
    canonicalPath: `/detail/${id ?? ""}`,
  });

  const handleFavorite = () => {
    if (!comic) return;
    const added = toggleFavorite(comic.id);
    setToast(added ? "已加入我的收藏" : "已取消收藏");
    setTimeout(() => setToast(""), 2000);
  };

  if (!comic) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">该漫画不存在或已下架</h1>
        <p className="mt-3 text-sm text-foreground-600">它可能已更名或被移除，去看看其他双女主韩漫吧。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to="/" className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
            返回首页
          </Link>
          <Link to="/category" className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300">
            浏览分类
          </Link>
        </div>
      </div>
    );
  }

  const firstChapter = comic.chapters[0];

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-2.5 text-sm text-background-50 shadow-lg">
          {toast}
        </div>
      )}

      <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/category" className="transition-colors hover:text-primary-600">漫画分类</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">{comic.title}</span>
      </nav>

      <section className="flex flex-col gap-8 lg:flex-row">
        <div className="w-full shrink-0 sm:w-64 lg:w-72">
          <div className="relative w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 aspect-[3/4]">
            <img
              src={comic.cover}
              alt={`${comic.title} 双女主韩漫封面大图`}
              title={`${comic.title} 漫画详情`}
              className="h-full w-full object-cover object-top"
            />
            <span
              className={`absolute left-3 top-3 rounded-md px-2.5 py-1 text-xs font-medium ${
                comic.status === "连载中" ? "bg-accent-500 text-background-50" : "bg-secondary-500 text-background-50"
              }`}
            >
              {comic.status}
            </span>
          </div>

          <div className="mt-4 flex flex-col gap-3">
            <Link
              to={`/reader/${comic.id}/${firstChapter.id}`}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-book-read-line text-lg" /> 开始阅读
            </Link>
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-medium transition-colors ${
                favorited
                  ? "border-accent-400 bg-accent-100 text-accent-700"
                  : "border-background-300 bg-background-50 text-foreground-800 hover:border-primary-300"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
              {favorited ? "已收藏" : "加入收藏"}
            </button>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(window.location.href);
                setToast("链接已复制，快分享给同好吧");
                setTimeout(() => setToast(""), 2000);
              }}
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300"
            >
              <i className="ri-share-forward-line" /> 分享作品
            </button>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{comic.title}</h1>

          <div className="mt-4 grid grid-cols-2 gap-4 rounded-lg border border-background-200 bg-background-50 p-4 sm:grid-cols-4">
            <div>
              <p className="text-xs text-foreground-500">作者</p>
              <p className="mt-1 text-sm font-semibold text-foreground-950">{comic.author}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">评分</p>
              <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-accent-600">
                <i className="ri-star-fill" /> {comic.rating}
              </p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">人气</p>
              <p className="mt-1 text-sm font-semibold text-foreground-950">{formatCount(comic.views)}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">更新时间</p>
              <p className="mt-1 text-sm font-semibold text-foreground-950">{comic.updatedAt}</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {comic.genres.map((genre) => (
              <Link
                key={genre}
                to={`/category?genre=${encodeURIComponent(genre)}`}
                className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
              >
                {genre}
              </Link>
            ))}
            {comic.tags.map((tag) => (
              <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                {tag}
              </span>
            ))}
          </div>

          <section className="mt-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">作品简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{comic.intro}</p>
          </section>

          <section className="mt-8">
            <button
              type="button"
              onClick={() => setChapterOpen((prev) => !prev)}
              className="flex w-full cursor-pointer items-center justify-between rounded-md border border-background-200 bg-background-100 px-4 py-3"
            >
              <span className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-list-check-2" /> 章节目录
                <span className="text-xs font-normal text-foreground-500">共 {comic.chapters.length} 话</span>
              </span>
              <i className={chapterOpen ? "ri-arrow-up-s-line text-xl" : "ri-arrow-down-s-line text-xl"} />
            </button>

            {chapterOpen && (
              <div className="mt-3 max-h-[420px] overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-2">
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {[...comic.chapters].reverse().map((chapter) => (
                    <Link
                      key={chapter.id}
                      to={`/reader/${comic.id}/${chapter.id}`}
                      className="flex items-center justify-between gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-primary-50"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <i className="ri-book-open-line shrink-0 text-primary-500" />
                        <span className="truncate">{chapter.title}</span>
                      </span>
                      <span className="shrink-0 text-xs text-foreground-400">{chapter.date}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
          <p className="mt-2 text-sm text-foreground-600">根据题材与热度为你匹配的双女主韩漫</p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4" data-product-shop>
            {related.map((item) => (
              <ComicCard key={item.id} comic={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}